/**
 * @file 学びメモの mermaid の記法を図 (SVG) にする。
 *
 * @what: mermaid を初めて使うときにだけ読み込み、安全側の設定 (securityLevel: strict) で SVG を作る。
 *   書き間違いは null を返す。同じ記法と配色の図は覚えておき、作り直さない。
 * @why: Notion と同じく、言語に mermaid と書いたコードの欄を図で読めるようにする (代表の指示、
 *   2026-11-03)。部品が大きいので、mermaid の欄があるメモを開いたときだけ読み込む。
 * @failure: 読み込みと図の作成の失敗は null を返し、呼ぶ側がもとの文字を出す。
 */

type Mermaid = (typeof import("mermaid"))["default"];

let loading: Promise<Mermaid> | null = null;
// 「並べる」では打つのを止めるたびに図を作るので、覚える数に上限を置き、古いものから捨てる
const CACHE_MAX = 40;
const cache = new Map<string, string>();
let seq = 0;

function loadMermaid(): Promise<Mermaid> {
  loading ??= import("mermaid").then((m) => m.default);
  return loading;
}

export type MermaidTheme = "default" | "dark";

const cacheKey = (code: string, theme: MermaidTheme) => `${theme}\n${code}`;

/** 覚えている図 (作ったことがなければ undefined)。 */
export function cachedMermaidSvg(code: string, theme: MermaidTheme): string | undefined {
  return cache.get(cacheKey(code, theme));
}

/** mermaid の記法を SVG の文字列にする。書き間違いや読み込みの失敗は null を返す。 */
export async function renderMermaid(code: string, theme: MermaidTheme): Promise<string | null> {
  const key = cacheKey(code, theme);
  const hit = cache.get(key);
  if (hit !== undefined) return hit;
  try {
    const mermaid = await loadMermaid();
    mermaid.initialize({ startOnLoad: false, securityLevel: "strict", theme });
    if ((await mermaid.parse(code, { suppressErrors: true })) === false) return null;
    seq += 1;
    const { svg } = await mermaid.render(`study-note-mermaid-${seq}`, code);
    cache.set(key, svg);
    if (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value!);
    return svg;
  } catch (err) {
    console.error("[study] failed to render mermaid", err);
    return null;
  }
}
